import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers, Controller as ControllerComponent } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { ActionsFooter } from "@/Component/ActionsFooter";
import BtnAsLink from "@/Component/BtnAsLink";
import { SelectCountry } from "@/Component/Form/SelectCountry";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useAssignInitialDeparturePilgrimsMutation, useExportInitialDepartureMutation, useGetInitialDepartureDetailQuery, useGetInitialDeparturePilgrimsQuery, useStoreInitialDepartureMutation } from "@/services/Departure";
import { DATE_TIME_FORMAT, DATE_TIME_FORMAT_INPUT, SYSTEMS } from "@/utils/constants";
import { cn, filterObject, handleFormErrors } from "@/utils/helpers";
import { useSystem } from "@/utils/hooks/useLocation";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { Cancel01Icon, FloppyDiskIcon, LicenseDraftIcon, PrinterIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Alert, Checkbox, Collapse, DatePicker, Divider, InputNumber, Space, Tag } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Stack } from "react-bootstrap";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Container, Label, Spinner } from "reactstrap";
import { Info } from "@/Component/Info";

export const ConfirmDepartureList = () => {
    const { t } = useTranslation();

    return (
        <Stack>
            <PageHeader title={t("CONFIRM_DEPARTURE_LIST")} />
            <Container fluid>
                <ConfirmDepartureListContent />
            </Container>
        </Stack >
    );
};

const ConfirmDepartureListContent = () => {
    const [openKeys, setOpenKeys] = useState([])
    const {
        filters,
        handleFilterApply,
    } = useFilter();
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const {
        searchValue,
        debouncedValue,
        setSearchValue,
    } = useDebouncedSearch()
    // ================================
    // SIDE EFFECTS
    // ================================
    const { refetch: refetchDepartureDetail } = useGetInitialDepartureDetailQuery({ enabled: true })
    const { data: initialDeparturePilgrimsRes, isLoading } = useGetInitialDeparturePilgrimsQuery({
        search: debouncedValue,
        page: currentPage,
        per_page: pageSize,
        ...filters
    });

    const { mutate: assignInitialDeparturePilgrims, isPending: isAssigningInitialDeparturePilgrims } = useAssignInitialDeparturePilgrimsMutation()

    const initialDeparturePilgrims = useMemo(() => initialDeparturePilgrimsRes?.data?.data ?? [], [initialDeparturePilgrimsRes]);
    const meta = initialDeparturePilgrimsRes?.data?.meta;
    const { t } = useTranslation();
    const form = useForm({ disabled: isLoading || isAssigningInitialDeparturePilgrims })

    const clearSelectedPilgrims = useCallback(() => {
        const pilgrims = form.getValues("pilgrims") || {}
        const state = Object.fromEntries(Object.entries(pilgrims).map(([key, value]) => [key, null]))
        form.setValue("pilgrims", state)
    }, [form])

    // useEffect(() => {
    //     if (!initialDeparturePilgrims.length) return;
    //     const prev = form.getValues("pilgrims") || {};
    //     const state = Object.fromEntries(
    //         initialDeparturePilgrims.map(p => [
    //             'pilgrim_' + p.application_no,
    //             p.bus_sequence ? p.application_no : (prev['pilgrim_' + p.application_no] ?? null)
    //         ])
    //     );
    //     form.setValue("pilgrims", { ...prev, ...state });
    // }, [initialDeparturePilgrims, form]);

    const columns = useMemo(() => [
        {
            key: "checkbox",
            name: <CheckAll data={initialDeparturePilgrims}
                disabled={isLoading}
                loading={isLoading}
                onChange={(e) => {
                    const prev = form.getValues("pilgrims")
                    const state = Object.fromEntries(initialDeparturePilgrims.map(p => ['pilgrim_' + p.application_no, e.target.checked ? p.application_no : null]))
                    form.setValue("pilgrims", { ...prev, ...state })
                }} />,
            render: (value, rowData) => {
                return <Controller
                    name={`pilgrims.pilgrim_${rowData.application_no}`}
                    control={form.control}
                    render={({ field }) => (
                        <Checkbox
                            key={rowData.application_no}
                            value={rowData.application_no}
                            className="square-checkbox"
                            checked={field.value}
                            onChange={(e) => e.target.checked ? field.onChange(e.target.value) : field.onChange(null)}
                        />
                    )}

                />
            },
            filterable: false,
        },
        {
            key: "application_no",
            name: t("CONFIRM_DEPARTURE_LIST_PILGRIM_NUMBER"),
        },
        {
            key: "full_name_en",
            name: t("CONFIRM_DEPARTURE_LIST_PILGRIM_NAME"),
        },
        {
            key: "bus_sequence",
            name: t("CONFIRM_DEPARTURE_LIST_VEHICLE_NUMBER"),
            render: (value) => value ? (t("VEHICLE") + " " + value) : "-",
        },
        {
            key: "nationality_id",
            name: t("CONFIRM_DEPARTURE_LIST_NATIONALITY"),
            render: (value) => {
                return <SelectCountry.Display value={value} />
            },
        },
        {
            key: "passport_no",
            name: t("CONFIRM_DEPARTURE_LIST_PASSPORT_NUMBER"),
        },
        {
            key: "visa_number",
            name: t("CONFIRM_DEPARTURE_LIST_VISA_NUMBER"),
        },
        {
            key: "campaign_number",
            name: t("CONFIRM_DEPARTURE_LIST_CAMPAIGN_GROUP_NUMBER"),
        },
        {
            key: "bag_number",
            name: t("CONFIRM_DEPARTURE_LIST_BAG_NUMBER"),
        },
        {
            key: "service_center_id",
            name: t("CONFIRM_DEPARTURE_LIST_SERVICE_CENTER"),
            render: (value) => {
                return <SelectCachedOptions.Display
                    optionKey="service_centers"
                    value={value}
                    campareBy="id"
                    render={(option) => option?.name}
                />
            },
        },
        {
            key: "status_code",
            name: t("CONFIRM_DEPARTURE_LIST_STATUS"),
            render: (value, rowData) => {
                return <Tag color={value === 1 ? "gray" : value === 2 ? "green" : "red"}>
                    {value === 1 ? t("DID_NOT_COME") : value === 2 ? t("EXIST") : t("DEPARTED")}
                </Tag>
            },
        },
    ], [initialDeparturePilgrims]);

    const onSubmit = (data) => {
        const selectedPilgrims = Object.values(data.pilgrims).filter(Boolean)
        const payload = {
            pilgrim_ids: selectedPilgrims,
            initial_departure_bus_id: data.initial_departure_bus_id
        }
        assignInitialDeparturePilgrims(payload, {
            onSuccess: () => {
                form.reset()
                refetchDepartureDetail()
            },
        })
    }

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value)
        setCurrentPage(1)
    }

    const assignDeparture = () => {
        onSubmit(form.getValues())
    }

    const filterFields = [
        {
            name: "application_no",
            label: t("CONFIRM_DEPARTURE_LIST_PILGRIM_NUMBER"),
            type: "input",
        },
        {
            name: "passport_no",
            label: t("CONFIRM_DEPARTURE_LIST_PASSPORT_NUMBER"),
            type: "input",
        },
        {
            name: "visa_number",
            label: t("CONFIRM_DEPARTURE_LIST_VISA_NUMBER"),
        },
        {
            name: "bag_id",
            label: t("CONFIRM_DEPARTURE_LIST_BAG_NUMBER"),
            type: "input",
        }
    ]

    const items = [
        {
            key: "1",
            title: t("VEHICLE_PLATE_NUMBER"),
            label: <h4>{t("تفاصيل الحملة")}</h4>,
            children: (
                <ConfirmDepartureListTable setOpenKeys={setOpenKeys} pilgrimsTotal={meta?.total} />
            )
        }
    ]

    return (
        <div>
            <Collapse items={items} activeKey={openKeys} onChange={(openKeys) => { setOpenKeys(openKeys) }} />

            <Divider />
            <FormProvider  {...form} clearSelectedPilgrims={clearSelectedPilgrims} assignDeparture={assignDeparture} >
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <TableC
                        data={initialDeparturePilgrims}
                        columns={columns}
                        loading={isLoading}
                        // search
                        searchValue={searchValue}
                        onSearchChange={handleSearchChange}
                        filterFields={filterFields}
                        filters={filters}
                        onFilterApply={handleFilterApply(() => setCurrentPage(1))}
                        pagination={{
                            current: currentPage || 1,
                            total: meta?.total || 0,
                            pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100],
                            onChange: (page, newPageSize) => {
                                setCurrentPage(page)
                                if (newPageSize != null) setPageSize(newPageSize)
                            },
                        }}
                    />
                    <DepartureActions isPending={isAssigningInitialDeparturePilgrims} assignDeparture={assignDeparture} refetchDepartureDetail={refetchDepartureDetail} />
                </form>
            </FormProvider>
        </div>

    )
}

const ConfirmDepartureListTable = ({ setOpenKeys, pilgrimsTotal }) => {
    const { t } = useTranslation()
    const { getLocalizedText, getLocalizedDate } = useLocalizedText()
    const { currentSystem } = useSystem()
    const isMakkah = currentSystem === SYSTEMS.makkah;

    // ================================
    // SIDE EFFECTS
    // ================================
    const { data: initialDepartureDetailRes, isLoading } = useGetInitialDepartureDetailQuery({ enabled: false })
    const initialDepartureDetail = initialDepartureDetailRes?.data?.data;
    const { mutate: exportInitialDeparture, isPending: isExportingInitialDeparture } = useExportInitialDepartureMutation()
    const { mutate: storeInitialDeparture, isPending: isStoringInitialDeparture, variables } = useStoreInitialDepartureMutation()
    const form = useForm({ disabled: isLoading });

    const resetForm = useCallback(() => {
        const { initial_departure: rawInitialDeparture, ...restInitialDepartureDetail } = initialDepartureDetail || {}
        const { id, ...initial_departure } = rawInitialDeparture ?? { id: null }
        const flatInitialDepartureDetail = { ...(restInitialDepartureDetail || {}), ...(initial_departure || {}) }
        const { id: departure_campaign_id, bus_number, buses: initialBuses, ...initialValues } = filterObject(flatInitialDepartureDetail, [
            "id",
            "means_id",
            "transport_company",
            "route",
            "driver_name",
            "port_id",
            "driver_id",
            "driver_bus_number",
            "flight_no",
            "exit_date",
            "flight_date",
            "service_center_id",
            "airline_id",
            "haj_count",
            "house_madina_contract_id",
            "transport_contract_no",
            "bus_count",
            "buses",
        ])

        const buses = initialBuses?.map?.(({ pilgrim_count }) => ({ pilgrim_count: String(pilgrim_count) })) ?? []

        const formFields = { ...initialValues, service_center_id: initialDepartureDetail?.arrival_campaign?.service_center_id, departure_campaign_id, bus_number, buses }
        form.reset(formFields)
    }, [initialDepartureDetail])

    useEffect(() => {
        resetForm()
    }, [initialDepartureDetail])

    const onSubmit = (payload) => {

        storeInitialDeparture(payload, {
            onError: (error) => {
                handleFormErrors(error, form.setError, t, "DEPARTURE_STORE_FAILED")
            }
        })
    }

    const validatePilgrimsCount = () => {
        const buses = form.getValues("buses")
        const haj_count = form.getValues("haj_count")
        const total_pilgrims = buses.reduce((acc, bus) => acc + (+(bus.pilgrim_count || 0)), 0)

        if (total_pilgrims !== +haj_count) {
            form.setError("buses", {
                type: "manual",
                message: t("PILGRIMS_COUNT_MISMATCH"),
            });

            return false
        }

        else {
            form.clearErrors("buses")
        }

        return true
    }

    const ctrls = [
        {
            labelProps: {
                children: `${t("CAMPAIGN_NUMBER")}/${t("GROUP_NUMBER")}`,
                htmlFor: "departure_campaign_id",
            },
            controllerProps: {
                name: "departure_campaign_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={`${t("CAMPAIGN_NUMBER")}/${t("GROUP_NUMBER")}`}
                    />
                ),

            },
            renderValue: () => <LabelValue.Value value={initialDepartureDetail?.campaign_id} />
        },
        {
            labelProps: {
                children: t("DEPARTURE_DIRECTION"),
                htmlFor: isMakkah ? "direction_id" : "location",
            },
            controllerProps: {
                name: isMakkah ? "direction_id" : "location",
                render: ({ field, fieldState: { invalid } }) =>
                    isMakkah ? (
                        <SelectCachedOptions
                            {...field}
                            id={field.name}
                            status={invalid ? "error" : undefined}
                            placeholder={t("DEPARTURE_DIRECTION")}
                            optionKey="directions"
                            options={(direction) => ({
                                value: String(direction?.id),
                                label: direction?.name,
                            })}
                        />
                    ) : (
                        <AntInput
                            {...field}
                            id={field.name}
                            status={invalid ? "error" : undefined}
                            placeholder={t("DEPARTURE_DIRECTION")}
                        />
                    ),
            },
            renderValue: (value) =>
                isMakkah ? (
                    <div
                        className={cn("d-inline-block",
                            value === 2
                                ? "border border-success rounded-3 p-2 bg-success-subtle text-success"
                                : "border border-danger rounded-3 p-2 bg-danger-subtle text-danger"
                        )}
                    >
                        <SelectCachedOptions.Display
                            optionKey="directions"
                            value={value}
                            campareBy="id"
                            render={(option) => option?.name}
                        />
                    </div>
                ) : (
                    value
                ),
        },
        {
            labelProps: {
                children: t("TRANSPORT_METHOD"),
                htmlFor: "means_id",
            },
            controllerProps: {
                name: "means_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("TRANSPORT_METHOD")}
                        optionKey="transportation"
                        options={(transportation) => ({
                            value: String(transportation?.id),
                            label: transportation?.name,
                        })}
                    />
                ),

            },
            renderValue: (value) => <SelectCachedOptions.Display
                value={value}
                campareBy="id"
                optionKey="transportation"
                render={(option) => option?.name}
            />
        },
        {
            labelProps: {
                children: t("TRANSPORT_COMPANY"),
                htmlFor: "transport_company",
            },
            controllerProps: {
                name: "transport_company",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("TRANSPORT_COMPANY")}
                        optionKey="buses"
                        options={(bus) => ({
                            value: String(bus.company_id ?? bus.id),
                            label: bus.company_name ?? bus.name ?? "-",
                        })}
                    />
                ),

            },
            renderValue: () => getLocalizedText(initialDepartureDetail?.transport_company_name_ar, initialDepartureDetail?.transport_company_name_en) + " - " + initialDepartureDetail?.transport_contract_no
        },
        {
            labelProps: {
                children: t("EXIT_PORT"),
                htmlFor: "port_id",
            },
            controllerProps: {
                name: "port_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("EXIT_PORT")}
                        optionKey="ports"
                        options={(port) => ({
                            value: String(port?.id),
                            label: getLocalizedText(port?.name_ar, port?.name_en) || port?.name,
                        })}
                    />
                ),

            },
            renderValue: (value) =>
                <SelectCachedOptions.Display
                    value={value}
                    campareBy="id"
                    optionKey="ports"
                    render={(port) => getLocalizedText(port?.name_ar, port?.name_en) || port?.name}
                />
        },
        {
            labelProps: {
                children: t("FLIGHT_NUMBER"),
                htmlFor: "flight_no",
            },
            controllerProps: {
                name: "flight_no",
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("FLIGHT_NUMBER")}
                    />
                ),

            }
        },
        {
            labelProps: {
                children: t("DEPARTURE_DATE"),
                htmlFor: "exit_date",
            },
            controllerProps: {
                name: "exit_date",
                render: ({ field, fieldState: { invalid } }) => (
                    <DatePicker
                        {...field}
                        id={field.name}
                        className="w-100"
                        size="large"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => field.onChange(date)}
                        placeholder={t("DEPARTURE_DATE")}
                        status={invalid ? "error" : undefined}
                        format={DATE_TIME_FORMAT_INPUT}
                    />
                ),

            },
            renderValue: (value) => getLocalizedDate(value, DATE_TIME_FORMAT)
        },
        {
            labelProps: {
                children: t("FLIGHT_DATE"),
                htmlFor: "flight_date",
            },
            controllerProps: {
                name: "flight_date",
                render: ({ field, fieldState: { invalid } }) => (
                    <DatePicker
                        {...field}
                        id={field.name}
                        className="w-100"
                        size="large"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(date) => field.onChange(date)}
                        placeholder={t("FLIGHT_DATE")}
                        status={invalid ? "error" : undefined}
                        format={DATE_TIME_FORMAT_INPUT}
                    />
                ),
            },
            renderValue: (value) => getLocalizedDate(value, DATE_TIME_FORMAT)
        },
        {
            labelProps: {
                children: t("SERVICE_CENTER"),
                htmlFor: "service_center_id",
            },
            controllerProps: {
                name: "service_center_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("SERVICE_CENTER")}
                        optionKey="service_centers"
                        options={(service_center) => ({
                            value: String(service_center?.id),
                            label: service_center?.name,
                        })}
                    />
                ),

            },
            renderValue: (value) => <SelectCachedOptions.Display
                value={value}
                campareBy="id"
                optionKey="service_centers"
                render={(service_center) => service_center?.name}
            />
        },
        {
            labelProps: {
                children: t("FLIGHT_AIRLINE"),
                htmlFor: "airline_id",
            },
            controllerProps: {
                name: "airline_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("FLIGHT_AIRLINE")}
                        optionKey="airlines"
                        options={(airline) => ({
                            value: String(airline?.id),
                            label: [
                                `(${airline?.code})`,
                                getLocalizedText(airline?.name_ar, airline?.name_en) || airline?.name,
                            ].join(" - "),
                        })}
                    />
                ),

            },
            renderValue: (value) => <SelectCachedOptions.Display
                value={value}
                campareBy="id"
                optionKey="airlines"
                render={(airline) => [
                    `(${airline?.code})`,
                    getLocalizedText(airline?.name_ar, airline?.name_en) || airline?.name,
                ].join(" - ")}
            />
        },
        {
            labelProps: {
                children: t("HOUSING_CONTRACT"),
                htmlFor: "house_madina_contract_id",
            },
            controllerProps: {
                name: "house_madina_contract_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("HOUSING_CONTRACT")}
                        optionKey="madinahContracts"
                        options={(contract) => ({
                            value: String(contract?.id),
                            label: [contract?.id, contract?.house_commercial_name_ar].join(" - "),
                        })}
                    />
                ),
                renderValue: (value) => <SelectCachedOptions.Display
                    optionKey="madinahContracts"
                    value={value?.id != null ? value.id : value}
                    campareBy="id"
                    render={(option) => option ? (
                        <Space>
                            <Info title={option?.house_commercial_name_ar} />
                            {option?.id}
                        </Space>
                    ) : "-"}
                />,
            },

        },
        {
            labelProps: {
                children: t("TOTAL_PILGRIMS"),
            },
            controllerProps: {
                name: "total_pilgrims",
            },
            isViewMode: true,
            renderValue: () => <LabelValue.Value value={pilgrimsTotal ?? "-"} />,
        },
    ]

    const editableCtrls = [

        {
            labelProps: {
                children: t("ROUTE"),
                htmlFor: "route",
                className: "required",
            },
            controllerProps: {
                name: "route",
                rules: {
                    required: t("FIELD_REQUIRED"),
                },
                render: ({ field, fieldState: { invalid } }) => (
                    <SelectCachedOptions
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("ROUTE")}
                        optionKey="transport_routes"
                        options={(route) => ({
                            value: String(route?.id),
                            label: [route?.id, getLocalizedText(route?.name_ar, route?.name_en)].join(" - "),
                        })}
                    />
                ),
            }
        },
        {
            labelProps: {
                children: t("DRIVER_NAME"),
                htmlFor: "driver_name",
            },
            controllerProps: {
                name: "driver_name",
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("DRIVER_NAME")}
                    />
                ),

            }
        },
        {
            labelProps: {
                children: t("ID_NUMBER"),
                htmlFor: "driver_id",
            },
            controllerProps: {
                name: "driver_id",
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("ID_NUMBER")}
                    />
                ),

            }
        },
        {
            labelProps: {
                children: t("VEHICLE_PLATE_NUMBER"),
                htmlFor: "driver_bus_number",
            },
            controllerProps: {
                name: "driver_bus_number",
                render: ({ field, fieldState: { invalid } }) => (
                    <AntInput
                        {...field}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("VEHICLE_PLATE_NUMBER")}
                    />
                ),

            }
        },
        {
            labelProps: {
                children: t("PILGRIMS_COUNT"),
                htmlFor: "haj_count",
                className: "required",
            },
            controllerProps: {
                name: "haj_count",
                rules: {
                    required: t("FIELD_REQUIRED"),
                },
                render: ({ field, fieldState: { invalid } }) => (
                    <InputNumber
                        {...field}
                        id={field.name}
                        type="number"
                        status={invalid ? "error" : undefined}
                        placeholder={t("PILGRIMS_COUNT")}
                    />
                ),

            }
        },
        {
            labelProps: {
                children: t("VEHICLES_COUNT"),
                htmlFor: "bus_number",
                className: "required",
            },
            controllerProps: {
                name: "bus_number",
                rules: {
                    required: t("FIELD_REQUIRED"),
                },
                render: ({ field, fieldState: { invalid } }) => (
                    <InputNumber
                        {...field}
                        max={10}
                        min={1}
                        id={field.name}
                        status={invalid ? "error" : undefined}
                        placeholder={t("VEHICLES_COUNT")}
                    />
                ),

            }
        },
    ]

    // update departure Transportions
    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Controllers
                    isLoading={isLoading}
                    isViewMode={true}
                    watch={form.watch}
                    {...{
                        controllers: ctrls.map((config) => ({
                            ...config,
                            labelProps: {
                                ...config.labelProps,
                                className: cn("fw-bold", config.labelProps.className),
                            },
                            controllerProps: {
                                ...config.controllerProps,
                                rules: {
                                    ...(config.controllerProps.rules || {}),
                                },
                                control: form.control,
                                errors: form.formState.errors,
                            },
                            layout: {
                                colProps: { xs: 6, md: 6 },
                            },
                        })),
                    }}
                />
                <Divider />
                <Controllers
                    isViewMode={initialDepartureDetail?.initial_departure?.status === "submitted"}
                    isLoading={isLoading}
                    watch={form.watch}
                    {...{
                        controllers: editableCtrls.map((config) => ({
                            ...config,
                            labelProps: {
                                ...config.labelProps,
                                className: cn("fw-bold", config.labelProps.className),
                            },
                            controllerProps: {
                                ...config.controllerProps,
                                rules: {
                                    ...(config.controllerProps.rules || {}),
                                },
                                control: form.control,
                                errors: form.formState.errors,
                            },
                            layout: {
                                colProps: { xs: 6, md: 6 },
                            },
                        })),
                    }}
                />
                <VehiclesCountController isViewMode={initialDepartureDetail?.initial_departure?.status === "submitted"} />
                {
                    initialDepartureDetail?.initial_departure?.buses?.length &&
                    <div className="border rounded-3 p-2 border-primary w-fit" style={{ borderStyle: "dashed !important" }}>
                    <Label className={cn("fw-bold d-block")}>{t("TOTAL_PILGRIMS_COUNT")}:</Label>
                    <Space>
                    {initialDepartureDetail?.initial_departure?.buses?.map((bus)=>(
                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold">{t("VEHICLE")} {bus.bus_sequence}:</span>
                            <span>{bus.assigned_pilgrim_count}</span>
                        </div>
                    ))}
                    </Space>
                    </div>
                }

                {!isLoading && <>
                    <Divider />
                    {initialDepartureDetail?.initial_departure?.status === "submitted" &&
                        <ActionsFooter>
                            <BtnAsLink
                                disabled={isExportingInitialDeparture}
                                type="button"
                                onClick={() => {
                                    exportInitialDeparture({
                                        number: initialDepartureDetail?.initial_departure?.number,
                                    })
                                }}
                                icon={isExportingInitialDeparture ? <Spinner animation="border" size="sm" /> : <HugeiconsIcon icon={PrinterIcon} />}
                                className="btn btn-sm btn-outline-primary"
                            >
                                {t("PRINT")}
                            </BtnAsLink>
                        </ActionsFooter>
                    }
                    {initialDepartureDetail?.initial_departure?.status !== "submitted" &&

                        <ActionsFooter>
                            <BtnAsLink
                                onClick={() => {
                                    const isValid = validatePilgrimsCount()
                                    if (!isValid) {
                                        return
                                    }

                                    form.setValue("status", "submitted")
                                }
                                }
                                disabled={isStoringInitialDeparture || isLoading}
                                icon={
                                    isStoringInitialDeparture && variables?.status === "submitted" ? <Spinner animation="border" size="sm" /> : <HugeiconsIcon icon={FloppyDiskIcon} />
                                }
                                className="btn btn-sm btn-primary" type="submit" >

                                {t("FINAL_SAVE")}
                            </BtnAsLink>
                            <BtnAsLink
                                onClick={() => {
                                    const isValid = validatePilgrimsCount()
                                    if (!isValid) {
                                        return
                                    }

                                    form.setValue("status", "draft")
                                }}
                                disabled={isStoringInitialDeparture || isLoading}
                                icon={isStoringInitialDeparture && variables?.status === "draft" ? <Spinner animation="border" size="sm" /> : <HugeiconsIcon icon={LicenseDraftIcon} />}
                                className="btn btn-sm btn-outline-primary"
                            >
                                {t("SAVE_AS_DRAFT")}
                            </BtnAsLink>
                            <BtnAsLink
                                style={{
                                    pointerEvents: isStoringInitialDeparture ? "none" : "auto",
                                }}
                                disabled={isStoringInitialDeparture || isLoading}
                                icon={<HugeiconsIcon icon={Cancel01Icon} />}
                                className="btn btn-sm btn-danger" type="button" onClick={() => { resetForm(); setOpenKeys(null) }}>
                                {t("CANCEL")}
                            </BtnAsLink>
                        </ActionsFooter>

                    }
                </>}
            </form>
        </FormProvider >
    )
}

const VehiclesCountController = ({ isViewMode }) => {
    const { t } = useTranslation()
    const form = useFormContext()
    const { confirm, ModalComponent } = useConfirm()
    const errors = form.formState.errors
    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "buses",
    })

    const vehiclesCount = form?.watch?.("bus_number")
    const vehicles = Array.from({ length: +vehiclesCount || 0 });
    const prevBusNumber = useRef(null)

    const updateBusesFields = () => {
        vehicles.forEach(() => {
            remove()
        })
        vehicles.forEach(() => {
            append({ pilgrim_count: "" }, {
                shouldFocus: false
            })
        })
    }
    useMemo(() => {
        const buses = form.getValues("buses")
        prevBusNumber.current = buses?.length ?? null

        if (prevBusNumber.current === Number(vehiclesCount)) {
            return
        }

        if (buses?.some?.((bus) => !!bus.pilgrim_count)) {
            confirm({
                title: t("CONFIRM_CHANGE_VEHICLES_COUNT"),
                content: t("CONFIRM_CHANGE_VEHICLES_COUNT_MESSAGE"),
                okText: t("CONFIRM"),
                cancelText: t("CANCEL"),
                onOk: () => {
                    updateBusesFields()
                },
                onCancel: () => {
                    form.setValue("bus_number", buses.length)
                },
            });
        } else {
            updateBusesFields()
        }

    }, [vehiclesCount])
    return (
        <Stack>
            <ModalComponent />

            {errors?.buses?.message && <Alert type="error" description={errors?.buses?.message} showIcon className="p-2 mb-3" />}

            <Stack gap={1}>
                <Label className={cn("fw-bold ", !isViewMode && "required")}>{t("PILGRIMS_COUNT_PER_VEHICLE")}:</Label>
                <Space gap={2} wrap>
                    {fields.map((item, index) => (
                        <ControllerComponent
                            key={item.id}
                            isViewMode={isViewMode}
                            watch={form.watch}
                            {...{
                                layout: {
                                    stackProps: { direction: "horizontal" },
                                },
                                labelProps: {
                                    className: "fw-bold",
                                    children: `${t("VEHICLE")} ${index + 1}: `,
                                },
                                controllerProps: {
                                    name: `buses.${index}.pilgrim_count`,
                                    control: form.control,
                                    rules: {
                                        required: t("FIELD_REQUIRED"),
                                    },

                                    render: ({ field: { name, ...field }, fieldState: { invalid, error } }) => (
                                        <span>
                                            <Space className="position-relative">
                                                <InputNumber
                                                    {...field}
                                                    min={1}
                                                    max={50}
                                                    status={invalid ? "error" : undefined}
                                                    type="number"
                                                    placeholder={t("PILGRIMS_COUNT")}
                                                />
                                                <div
                                                    role="button"
                                                    onClick={() => {
                                                        remove(index)
                                                        const buses = Number(form.getValues("bus_number"))
                                                        const newBuses = buses - 1
                                                        form.setValue("bus_number", newBuses <= 0 ? "" : newBuses)
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        top: "0",
                                                        right: "0",
                                                        border: "2px solid #fecacb",
                                                        display: "flex",
                                                        borderRadius: "10000px",
                                                        width: "20px",
                                                        height: "20px",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        transform: "translate(50%, -50%)",
                                                        background: "#fef2f2",
                                                        color: "#991b1d",
                                                        cursor: "pointer",
                                                    }}>
                                                    <HugeiconsIcon icon={Cancel01Icon} />
                                                </div>
                                            </Space>
                                            {invalid && error?.message && <div className="text-danger">
                                                {error?.message}
                                            </div>}
                                        </span>
                                    )
                                },
                            }}

                        />
                    ))}
                </Space>
            </Stack>
        </Stack>

    )
}

const CheckAll = (props) => {
    const { data, loading, ...restProps } = props

    const form = useFormContext()
    const selectedPilgrims = form.watch("pilgrims");
    const isAllSelected = data.every((p) => !!selectedPilgrims?.[`pilgrim_${p.application_no}`]);
    const pagePartiallySelected = !isAllSelected && data.some((p) => !!selectedPilgrims?.[`pilgrim_${p.application_no}`]);

    return (
        <Checkbox
            {...restProps}
            indeterminate={pagePartiallySelected}
            checked={!loading && isAllSelected}
            className="square-checkbox"
        />
    )
}

const DepartureActions = ({ isPending, assignDeparture, refetchDepartureDetail }) => {
    const [isOpen, setIsOpen] = useState(false)
    const form = useFormContext()
    const { t } = useTranslation()

    const pilgrims = form.watch("pilgrims") || {}
    const selectedPilgrims = Object.values(pilgrims).filter(Boolean)

    if (selectedPilgrims.length <= 0) {
        return null
    }

    const handleOpenConfirmModal = () => {
        setIsOpen(true)
    }

    return (
        <>
            <ConfirmDepartureModal isPending={isPending} isOpen={isOpen} onToggle={() => { setIsOpen(false) }} assignDeparture={assignDeparture} />
            <ActionsFooter>
                <Space>

                    <button className="btn btn-sm btn-primary" type="button" onClick={handleOpenConfirmModal} >
                        {t("CONFIRM_DEPARTURE")}
                    </button>
                </Space>

                <Button className="btn btn-sm btn-danger" type="button" variant="danger" onClick={() => { form.reset() }}>
                    {t("CANCEL")}
                </Button>
            </ActionsFooter>
        </>
    );
};

const ConfirmDepartureModal = ({ isOpen, onToggle, isPending, assignDeparture }) => {
    const { t } = useTranslation()
    const form = useFormContext()

    // ================================
    // SIDE EFFECTS
    // ================================
    const { data: initialDepartureDetailRes, isLoading } = useGetInitialDepartureDetailQuery({ enabled: false })
    const initialDepartureDetail = initialDepartureDetailRes?.data?.data;

    const buses = initialDepartureDetail?.initial_departure?.buses;

    return (
        <Modal show={isOpen} onToggle={onToggle} centered backdrop="static" keyboard={false}>
            <Modal.Header>
                {t("CONFIRM_DEPARTURE")}
            </Modal.Header>

            <Modal.Body>
                <Controller
                    name="initial_departure_bus_id"
                    control={form.control}
                    rules={{
                        required: t("FIELD_REQUIRED")
                    }}
                    render={({ field }) => (
                        <Stack >
                            <Label htmlFor="bus_id">
                                {t("CONFIRM_DEPARTURE_LIST_VEHICLE_NUMBER")}
                            </Label>

                            <AntSelect
                                loading={isLoading}
                                {...field}
                                disabled={isLoading}
                                id={field.name}
                                className="w-100"
                                placeholder={t("CONFIRM_DEPARTURE_LIST_VEHICLE_NUMBER")}
                                options={buses?.map?.((bus) => ({
                                    value: bus.id,
                                    label: t("VEHICLE") + " " + bus.bus_sequence,
                                }))}
                            />
                        </Stack>
                    )}
                />

            </Modal.Body>
            <Modal.Footer>
                <Button disabled={isPending} className="btn btn-sm btn-primary" type="button" variant="primary" onClick={assignDeparture}>
                    {isPending ? <Spinner animation="border" size="sm" /> : t("CONFIRM")}
                </Button>
                <Button disabled={isPending} style={{ pointerEvents: isPending ? "none" : "auto" }} className="btn btn-sm btn-danger" type="button" variant="danger" onClick={onToggle}>
                    {t("CANCEL")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}